import { For, Show, createMemo, createSignal, type Component } from 'solid-js';

import { castSkill, usePotion } from '../net/protocol';
import type { GameStore } from '../state/store';
import { Icon } from './Icon';
import { FloatingTooltip } from './ItemTooltip';
import { skillRows } from './skills';
import { SkillTooltipBody } from './SkillTooltip';
import {
  BAR_SLOTS,
  BELT_KEY,
  beltRefusalText,
  dodgeRefusalText,
  refusalText,
  sellRefusalText,
  buyRefusalText,
  gateRefusalText,
  stationRefusalText,
  slotKeyLabel,
  slotSigil,
  ACTION_ICONS,
  LMB_SLOT,
  STRIKE_SIGIL,
  slotTooltip,
} from './actionbar';
import { useDrag } from './DragContext';
import { containerRefusalText } from './drag';
import { DodgeIndicator } from './DodgeIndicator';
import { CRITICAL_HEALTH, VitalOrb, percent } from './VitalOrb';

// Re-exported because they were this module's exports before the orb became its
// own component, and both are imported by name from here in the suite and in
// `DodgeIndicator`. One definition, in `VitalOrb.tsx`, beside the orb it describes.
export { CRITICAL_HEALTH, percent };

/**
 * The always-visible bar: health orb, potion belt, skill row, roll, mana orb.
 *
 * **Both vitals are orbs, bracketing the bar.** Mana used to be an 8px line under
 * the skill row while health was a 96px vessel on the left, so one of the two
 * resources every cast spends was legible at a glance and the other had to be
 * hunted for. They are one component now — see `VitalOrb.tsx`.
 *
 * The primary slot's cooldown sweep is driven by the server's authoritative
 * `cooldown` field, not a local timer — if the server is lagging, the sweep
 * lags with it, which is the honest thing for it to do.
 */
export const ActionBar: Component<{ store: GameStore }> = (props) => {
  const dragging = useDrag();
  const self = () => props.store.self();
  const stamina = () => props.store.stamina();
  const mana = () => props.store.mana();
  const belt = () => props.store.belt();

  const healthFraction = () => {
    const me = self();
    return me === undefined || me.maxHp <= 0 ? 0 : me.hp / me.maxHp;
  };

  /**
   * How much of a slot's cooldown is left, 0 → 1.
   *
   * **Counted down on the client**, from the last CHARACTER frame's number and the instant it
   * arrived — `store.skillCooldown`. Reading `skillState(id).cooldown` directly is what this
   * replaced, and it was frozen: the server counts cooldowns down every tick and marks the
   * CHARACTER frame dirty for none of it, because that frame carries the whole stat block plus
   * every skill and passive and sending it at 30 Hz per player would undo the bandwidth
   * budget. So the sweep showed the value from the moment of the cast until something else
   * happened to mark the frame — reported as *the overlay only moves when we cast a spell*.
   *
   * The basic attack is different and stays as it was: its cooldown rides the 30 Hz snapshot,
   * so it already gets a fresh authoritative number every tick.
   */
  const sweepFor = (index: number) => {
    const id = props.store.slotted()[index];
    if (!id) return 0;

    const remaining = props.store.skillCooldown(id);
    const total = props.store.skillInfo(id)?.cooldownSec ?? 0;
    return total > 0 ? Math.min(1, remaining / total) : 0;
  };

  /** What is actually in a bar slot: the server's assignment, or nothing. */
  const boundSkill = (index: number) => {
    const id = props.store.slotted()[index];
    return id ? props.store.skillInfo(id) : undefined;
  };

  /**
   * The full rows, by skill id, for the tooltip.
   *
   * **Memoised, because `skillRows` walks the whole hundred-skill catalogue.** Building it per slot
   * would be five or six passes over it on every frame the bar re-renders, for five answers that come
   * from the same input.
   *
   * The bar needs a whole row and not just a `SkillInfo` because half of what a tooltip says is about
   * *this player*: the rank they bought, what the next one costs, whether the gate is met and by how
   * much it is missed. See `SkillTooltipBody`.
   */
  const rowsById = createMemo(() => {
    const c = props.store.character();
    const rows = skillRows(
      props.store.skillCatalogue(),
      c?.skills ?? [],
      c?.attributes.total ?? null,
      c?.slotted ?? [],
      c?.progression.skillPoints ?? 0,
    );
    return new Map(rows.map((row) => [row.def.skillId, row]));
  });

  const rowFor = (index: number) => {
    const id = props.store.slotted()[index];
    return id ? rowsById().get(id) : undefined;
  };

  /** Which slot the pointer is resting on, for the tooltip. Null when it is on none. */
  const [hoveredSlot, setHoveredSlot] = createSignal<number | null>(null);
  const [slotAnchor, setSlotAnchor] = createSignal<HTMLElement | undefined>();

  /**
   * A slot is pressable whether or not it holds a skill.
   *
   * Disabling an empty one is what made this bar feel broken: the player pressed
   * it, nothing happened, and nothing said why. Now the press goes to the server
   * and comes back as a refusal the bar can explain.
   */
  // Aimed at the cursor, exactly like the number keys — a click on the bar and a
  // press of its key must do the same thing.
  const cast = (index: number) =>
    props.store.send(castSkill(index, props.store.cursorWorld() ?? undefined));

  return (
    <Show when={self()}>
      {(me) => (
        <div class="action-bar hud-interactive" data-testid="action-bar">
          <VitalOrb
            vital="health"
            label="Health"
            current={me().hp}
            max={me().maxHp}
            critical={healthFraction() > 0 && healthFraction() < CRITICAL_HEALTH}
            testId="health-orb"
            valueTestId="health-value"
          />

          {/*
            The potion belt, beside the health orb because that is what it refills.

            **One key, not four.** The written design puts four potions on keys 1-4,
            which this bar already owns; four keys only mean something once there are
            four different draughts, which needs alchemy. See internal/game/potion.go.

            Null before the first CHARACTER frame, and then it renders nothing rather
            than a plausible full belt — the same rule the stamina bar follows. A
            widget showing charges the player may not have is a lie they cannot see
            through.
          */}
          <Show when={belt()}>
            {(b) => (
              <button
                type="button"
                class="belt"
                classList={{
                  'belt--empty': b().charges <= 0,
                  'belt--cooling': (b().cooldown ?? 0) > 0,
                }}
                data-testid="potion-belt"
                aria-label={`Potion belt, ${b().charges} of ${b().capacity} charges`}
                title={
                  b().charges > 0
                    ? `Drink (${BELT_KEY}) — restores ${b().healPerCharge}`
                    : 'The belt is empty. Only town refills it.'
                }
                onClick={() => props.store.send(usePotion())}
              >
                {/* The flask sits behind the count rather than instead of it: charges are
                    the number a player reads mid-fight, and an icon that replaced them
                    would be prettier and useless. Absolutely positioned, so the button's
                    box is unchanged. */}
                <Icon class="belt__art" iconId={ACTION_ICONS.potion} glyph="" />
                <span class="belt__charges" data-testid="belt-charges">
                  {b().charges}/{b().capacity}
                </span>
                <span class="slot__key">{BELT_KEY}</span>
                {/* The cooldown is the server's remaining time, so a lagging server
                    shows a lagging sweep — the honest thing for it to do. */}
                <Show when={(b().cooldown ?? 0) > 0}>
                  <span class="belt__cooling" data-testid="belt-cooldown" />
                </Show>
              </button>
            )}
          </Show>

          <div class="action-skills">
            {/* Why the last cast did not happen. Sits above the row so it reads
                as an answer to the button that was just pressed. */}
            <Show when={props.store.notice()}>
              {(notice) => (
                <div class="action-skills__notice" data-testid="skill-notice" role="status">
                  {notice().kind === 'potion'
                    ? beltRefusalText(notice().reason)
                    : notice().kind === 'sell'
                      ? sellRefusalText(notice().reason)
                      : notice().kind === 'dodge'
                        ? dodgeRefusalText(notice().reason)
                        : notice().kind === 'station'
                          ? stationRefusalText(notice().reason)
                          : notice().kind === 'gate'
                            ? gateRefusalText(notice().reason)
                            : notice().kind === 'buy'
                              ? buyRefusalText(notice().reason)
                              : notice().kind === 'container'
                                ? containerRefusalText(notice().reason, notice().container)
                                : refusalText(notice().reason, slotKeyLabel(notice().slot))}
                </div>
              )}
            </Show>

            <div class="action-skills__row">
              {/*
                **The basic attack is no longer a plate.**

                It was a `<div>`, not a button, with a comment saying a button labelled LMB
                would be two ways to do one thing. That was true while the left button could
                only ever swing a weapon — and it is what made the left button the single
                input a player could not rebind, drawn as furniture beside six slots that were
                theirs to arrange.

                So LMB is a slot like the rest (`LMB_SLOT`), and the basic attack is what that
                slot *falls back to* while it is empty — which is the default, so nothing
                changes for a player who assigns nothing. Below, `isBasic` is the whole of the
                difference: the strike icon instead of a skill's, the weapon-swing cooldown
                instead of a skill's, and no lock, because an empty LMB is not an empty slot.
              */}
              <For each={BAR_SLOTS}>
                {(slot) => {
                  const bound = () => boundSkill(slot.index);
                  const name = () => bound()?.name;
                  const rank = () => {
                    const id = props.store.slotted()[slot.index];
                    return id ? (props.store.skillState(id)?.rank ?? 0) : 0;
                  };
                  /** The left button with nothing assigned: still the basic attack. */
                  const isBasic = () => slot.index === LMB_SLOT && bound() === undefined;
                  const sweep = () =>
                    isBasic() ? (me().cooldown ?? 0) : sweepFor(slot.index);

                  return (
                    <button
                      type="button"
                      class="slot"
                      classList={{
                        'slot--ready': bound() !== undefined,
                        'slot--locked': bound() === undefined && !isBasic(),
                        'slot--basic': isBasic(),
                        // Lit while a dragged skill is over it, so the player can see
                        // where it will land before letting go.
                        'slot--drop': dragging.hoveredSlot() === slot.index,
                      }}
                      data-testid={`slot-${slot.index}`}
                      title={
                        isBasic()
                          ? 'Basic attack. Drag a skill here to attack with that instead.'
                          : slotTooltip(name(), slot.key, bound()?.castTimeSec, rank())
                      }
                      aria-label={
                        isBasic()
                          ? 'Basic attack, on the left mouse button'
                          : (name() ?? `Skill slot ${slot.key}, empty`)
                      }
                      // **An empty left button is not castable.** `cast` on an empty slot earns
                      // a server refusal, and the HUD words it "Nothing is slotted on LMB" —
                      // which is false about the button that swings your weapon, and would be
                      // the answer to a question the player never asked. In that one state the
                      // slot is a cooldown readout, which is what `cursor: default` says.
                      onClick={() => {
                        if (!isBasic()) cast(slot.index);
                      }}
                      // A slot is a drop target for a skill dragged out of the panel.
                      // `pointerenter`/`leave` rather than a hit test on release: the
                      // highlight has to follow the pointer the whole way, and jsdom
                      // computes no layout for anything geometric to work from.
                      onPointerEnter={(e) => {
                        if (dragging.skillDrag()) dragging.hoverSlot(slot.index);
                        // The rich card, for a slot that holds something. Separate from the drag
                        // highlight above: a tooltip is wanted whether or not a drag is in flight.
                        setSlotAnchor(e.currentTarget);
                        setHoveredSlot(slot.index);
                      }}
                      onPointerLeave={() => {
                        if (dragging.hoveredSlot() === slot.index) dragging.hoverSlot(null);
                        if (hoveredSlot() === slot.index) setHoveredSlot(null);
                      }}
                      // Runs before the window listener in `DragLayer`, which is what
                      // makes a release *here* mean this slot and a release anywhere
                      // else mean cancel.
                      onPointerUp={() => dragging.dropSkill(slot.index)}
                    >
                      {/* The catalogue's art where this build has it, and the skill's
                          initial where it does not — see `Icon`. The initial is not a
                          placeholder: a hundred skills share a couple of dozen letters,
                          but a letter still says *which slot changed* when the bar is
                          rearranged, and it is what a client renders for a skill added
                          after it was built. */}
                      <Icon
                        class="slot__sigil"
                        iconId={bound()?.iconId ?? (isBasic() ? ACTION_ICONS.strike : undefined)}
                        glyph={isBasic() ? STRIKE_SIGIL : slotSigil(name())}
                      />
                      <span class="slot__key">{slot.key}</span>
                      <Show when={sweep() > 0}>
                        <span
                          class="slot__cooldown cd-sweep"
                          data-testid={isBasic() ? 'cooldown-strike' : `cooldown-${slot.index}`}
                          style={{ '--sweep': String(sweep()) }}
                        />
                        {/* **The number, not just the wedge** — the half the dodge button had
                            and this did not, and the reason the dodge read better. A sweep says
                            *soon*; a player timing a cast against a monster's swing needs *how
                            soon*. One decimal, like the dodge's, because a cooldown is measured
                            in seconds and tenths are what a rotation is timed in. */}
                        {/* Numbers come from the skill's own cooldown, so the basic attack has
                            none to print: `skillCooldown('')` is 0, and "0.0" over a sweeping
                            wedge is a readout that contradicts the thing beside it. */}
                        <Show when={!isBasic()}>
                          <span class="slot__timer cd-timer" data-testid={`cooldown-timer-${slot.index}`}>
                            {props.store.skillCooldown(props.store.slotted()[slot.index] ?? '').toFixed(1)}
                          </span>
                        </Show>
                      </Show>
                    </button>
                  );
                }}
              </For>
            </div>

            {/*
              **One tooltip for the whole bar, not one per slot.** The card is a portal anchored to
              whichever slot the pointer is on, so five slots share one instance — five `FloatingTooltip`s
              would be five portals mounted at all times for one that can ever be visible.

              `SkillTooltipBody` is the same component the skills panel renders. The bar used to have a
              native `title=` string with a name, a rank and a cast time, and none of the description,
              cost, next-rank gain or gate — so the panel explained a skill and the bar, which is where a
              player looks mid-fight, did not.

              The `title` attribute stays on each slot: it is what an empty slot says ("press 3 to see
              what it needs"), and it is the only thing a keyboard or touch user gets.
            */}
            <Show when={hoveredSlot() !== null && rowFor(hoveredSlot()!)}>
              {(row) => (
                <FloatingTooltip
                  anchor={slotAnchor()}
                  when
                  class="skills__tip"
                  testId={`slot-tip-${hoveredSlot()}`}
                >
                  <SkillTooltipBody row={row()} store={props.store} />
                </FloatingTooltip>
              )}
            </Show>

            <div
              class="stamina"
              classList={{ 'stamina--dormant': !stamina().live }}
              data-testid="stamina"
              role="progressbar"
              aria-label="Stamina"
              aria-valuenow={stamina().current}
              aria-valuemin={0}
              aria-valuemax={stamina().max}
            >
              <div
                class="stamina__fill"
                style={{ width: `${percent(stamina().current, stamina().max)}%` }}
              />
              {/* Before the CHARACTER frame there is no number to show. Saying so
                  beats a full bar the player cannot tell is invented. */}
              <Show when={!stamina().live}>
                <span class="stamina__note" data-testid="stamina-dormant">
                  Stamina · dormant
                </span>
              </Show>
            </div>

          </div>

          {/*
            The roll, beside the stamina it spends.

            A *column* rather than a row inside `.action-skills`: the bar's height is
            set by the 96px orbs, so a new row would make the whole `.hud-interactive`
            strip taller — and a taller strip at the bottom centre of an isometric view
            swallows clicks where the character stands. `grid-auto-flow: column` on
            `.action-bar` takes another child without a template that counts them.
          */}
          <DodgeIndicator store={props.store} />

          {/*
            Mana, mirroring health across the bar.

            Skills deduct it on every cast, so a player who cannot afford one has to be
            able to see why — and until this became an orb it was an 8px line under the
            skill row, which is not something anyone reads mid-fight.

            Absent, not empty, before the CHARACTER frame: there is no number then, and
            a full-looking orb the player cannot tell is invented is the lie the stamina
            bar's dormant label exists to avoid. The `max > 0` arm is unreachable today
            (the baseline INT of 10 gives every character a pool) and is kept because
            it is what makes the guard a statement about the data rather than about the
            current baseline.
          */}
          <Show when={mana().live && mana().max > 0}>
            <VitalOrb
              vital="mana"
              label="Mana"
              current={mana().current}
              max={mana().max}
              testId="mana"
              valueTestId="mana-value"
            />
          </Show>
        </div>
      )}
    </Show>
  );
};
